import React, { useState, useEffect } from 'react';
import { Shield, Users, Save, AlertCircle, CheckCircle, Loader } from 'lucide-react';
import api from '../../lib/api';

const MODULOS_DISPONIBLES = [
  // Módulos para Admin y Técnicos
  { id: 'dashboard', label: 'Dashboard', descripcion: 'Vista general del sistema' },
  { id: 'pendientes', label: 'Pendientes', descripcion: 'Gestión de tareas y pendientes asignados' },
  { id: 'tickets', label: 'Tickets Soporte', descripcion: 'Gestión de tickets de soporte' },
  { id: 'ordenes', label: 'Órdenes de Servicio', descripcion: 'Gestión de órdenes de trabajo' },
  { id: 'presupuestos', label: 'Presupuestos', descripcion: 'Creación y seguimiento de presupuestos' },
  { id: 'clientes', label: 'Clientes', descripcion: 'Gestión de clientes' },
  { id: 'equipos', label: 'Equipos', descripcion: 'Administración de equipos' },
  { id: 'agendamientos', label: 'Agendamiento', descripcion: 'Programación de servicios y visitas' },
  { id: 'agendamientos_inteligentes', label: 'IA Agendamientos', descripcion: 'Agendamiento inteligente con IA' },
  { id: 'usuarios', label: 'Usuarios', descripcion: 'Gestión de usuarios del sistema (solo admin)' },
  { id: 'panelAvanzado', label: 'Panel Avanzado', descripcion: 'Configuración avanzada del sistema (solo admin)' },
  { id: 'recepciones_equipos', label: 'Recepción de Equipos', descripcion: 'Ingreso y entrega de equipos en taller' },
  { id: 'entregas_documentos', label: 'Entrega de Documentos', descripcion: 'Registro de entrega de recibos y facturas' },
  { id: 'movilidad', label: 'Planilla de Movilidad', descripcion: 'Registro de pasajes y combustible por técnico' },
  
  // Módulos especiales para Clientes
  { id: 'dashboard_cliente', label: 'Dashboard Cliente', descripcion: 'Vista inicial para clientes' },
  { id: 'monitor_servicios', label: 'Estado del Sistema', descripcion: 'Página de estado (Uptime Kuma)' },
  { id: 'mis_ordenes', label: 'Mis Órdenes', descripcion: 'Órdenes del cliente' },
  { id: 'mis_equipos', label: 'Mis Equipos', descripcion: 'Equipos del cliente' },
  { id: 'mis_agendamientos', label: 'Mis Agendamientos', descripcion: 'Agendamientos del cliente' },
  { id: 'mis_horas', label: 'Mis Horas', descripcion: 'Horas de servicio del cliente' },
];

export default function GestionPermisos() {
  const [usuarios, setUsuarios] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [permisos, setPermisos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mensaje, setMensaje] = useState(null);

  useEffect(() => {
    cargarUsuarios();
  }, []);

  const cargarUsuarios = async () => {
    try {
      setLoading(true);
      const res = await api.get('/usuarios');
      // Filtrar solo el usuario "admin" específico, permitir todos los demás
      const usuariosConfigurables = res.data.filter(u => u.username !== 'admin');
      setUsuarios(usuariosConfigurables);
      setMensaje(null);
    } catch (err) {
      console.error('Error al cargar usuarios:', err);
      setMensaje({ tipo: 'error', texto: 'Error al cargar usuarios' });
    } finally {
      setLoading(false);
    }
  };

  const cargarPermisosUsuario = async (usuarioId) => {
    try {
      const res = await api.get(`/usuarios/${usuarioId}/permisos`);
      setPermisos(res.data.modulos_visibles || []);
    } catch (err) {
      console.error('Error al cargar permisos:', err);
      setPermisos([]);
    }
  };

  const handleSelectUser = async (usuario) => {
    setSelectedUser(usuario);
    // Usar usuario.id o usuario._id según lo que venga del backend
    const usuarioId = usuario.id || usuario._id;
    await cargarPermisosUsuario(usuarioId);
  };

  const togglePermiso = (moduloId) => {
    setPermisos(prev => {
      if (prev.includes(moduloId)) {
        return prev.filter(m => m !== moduloId);
      } else {
        return [...prev, moduloId];
      }
    });
  };

  const handleGuardar = async () => {
    if (!selectedUser) return;
    
    // Verificar que no sea el usuario admin principal
    if (selectedUser.username === 'admin') {
      setMensaje({ 
        tipo: 'error', 
        texto: 'No se pueden editar los permisos del usuario administrador principal' 
      });
      return;
    }
    
    try {
      setSaving(true);
      // Usar selectedUser.id o selectedUser._id según lo que venga del backend
      const usuarioId = selectedUser.id || selectedUser._id;
      await api.put(`/usuarios/${usuarioId}/permisos`, {
        modulos_visibles: permisos
      });
      
      setMensaje({ 
        tipo: 'success', 
        texto: `Permisos actualizados para ${selectedUser.nombre_completo || selectedUser.username}` 
      });
      
      // Limpiar mensaje después de 3 segundos
      setTimeout(() => setMensaje(null), 3000);
    } catch (err) {
      console.error('Error al guardar permisos:', err);
      setMensaje({ 
        tipo: 'error', 
        texto: err.response?.data?.detail || 'Error al guardar permisos. Verifica que el usuario sea editable.' 
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSeleccionarTodos = () => {
    setPermisos(MODULOS_DISPONIBLES.map(m => m.id));
  };

  const handleLimpiarTodos = () => {
    setPermisos([]);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="animate-spin text-emerald-500" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 rounded-lg shadow-lg p-6 text-white">
        <div className="flex items-center gap-3">
          <Shield size={32} />
          <div>
            <h2 className="text-2xl font-bold">Gestión de Permisos</h2>
            <p className="text-emerald-100 text-sm mt-1">
              Controla qué módulos puede ver cada usuario
            </p>
          </div>
        </div>
      </div>

      {/* Mensaje de feedback */}
      {mensaje && (
        <div className={`rounded-lg p-4 flex items-center gap-3 ${
          mensaje.tipo === 'success' 
            ? 'bg-green-50 text-green-800 border border-green-200' 
            : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          {mensaje.tipo === 'success' ? (
            <CheckCircle size={20} />
          ) : (
            <AlertCircle size={20} />
          )}
          <span>{mensaje.texto}</span>
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-6">
        {/* Panel de Usuarios */}
        <div className="md:col-span-1">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center gap-2 mb-4 pb-3 border-b">
              <Users size={20} className="text-gray-600" />
              <h3 className="font-semibold text-gray-900">Usuarios</h3>
              <span className="ml-auto text-xs bg-gray-100 px-2 py-1 rounded">
                {usuarios.length}
              </span>
            </div>
            
            <div className="space-y-2 max-h-[500px] overflow-y-auto">
              {usuarios.map(usuario => {
                const usuarioKey = usuario.id || usuario._id;
                const selectedKey = selectedUser?.id || selectedUser?._id;
                return (
                  <button
                    key={usuarioKey}
                    onClick={() => handleSelectUser(usuario)}
                    className={`w-full text-left p-3 rounded-lg transition-all ${
                      selectedKey === usuarioKey
                        ? 'bg-emerald-50 border-2 border-emerald-500'
                        : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                    }`}
                  >
                    <div className="font-medium text-gray-900">
                      {usuario.nombre_completo || usuario.username}
                    </div>
                    <div className="text-xs text-gray-500 mt-1 flex items-center gap-2">
                      <span className={`px-2 py-0.5 rounded ${
                        usuario.rol === 'tecnico' 
                          ? 'bg-green-100 text-green-700'
                          : 'bg-purple-100 text-purple-700'
                      }`}>
                        {usuario.rol}
                      </span>
                      {usuario.activo ? (
                        <span className="text-green-600">● Activo</span>
                      ) : (
                        <span className="text-red-600">● Inactivo</span>
                      )}
                    </div>
                  </button>
                );
              })}
              
              {usuarios.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Users size={32} className="mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No hay usuarios configurables</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Panel de Permisos */}
        <div className="md:col-span-2">
          {selectedUser ? (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-6 pb-4 border-b">
                <div>
                  <h3 className="font-semibold text-gray-900 text-lg">
                    Permisos de {selectedUser.nombre_completo || selectedUser.username}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Selecciona los módulos que este usuario puede visualizar
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleSeleccionarTodos}
                    className="text-xs px-3 py-1.5 bg-green-50 text-green-700 hover:bg-green-100 rounded-lg transition-colors"
                  >
                    Todos
                  </button>
                  <button
                    onClick={handleLimpiarTodos}
                    className="text-xs px-3 py-1.5 bg-red-50 text-red-700 hover:bg-red-100 rounded-lg transition-colors"
                  >
                    Ninguno
                  </button>
                </div>
              </div>

              {/* Grid de Módulos */}
              <div className="grid md:grid-cols-2 gap-3 mb-6">
                {MODULOS_DISPONIBLES.map(modulo => (
                  <label
                    key={modulo.id}
                    className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      permisos.includes(modulo.id)
                        ? 'bg-emerald-50 border-emerald-500'
                        : 'bg-gray-50 border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={permisos.includes(modulo.id)}
                      onChange={() => togglePermiso(modulo.id)}
                      className="mt-1 h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">{modulo.label}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{modulo.descripcion}</div>
                    </div>
                  </label>
                ))}
              </div>

              {/* Resumen */}
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="text-sm text-gray-600">
                  <strong>{permisos.length}</strong> de <strong>{MODULOS_DISPONIBLES.length}</strong> módulos seleccionados
                </div>
              </div>

              {/* Botón Guardar */}
              <button
                onClick={handleGuardar}
                disabled={saving}
                className="w-full bg-gradient-to-r from-emerald-600 to-emerald-700 text-white py-3 rounded-lg font-medium hover:from-emerald-700 hover:to-emerald-800 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <>
                    <Loader className="animate-spin" size={20} />
                    Guardando...
                  </>
                ) : (
                  <>
                    <Save size={20} />
                    Guardar Permisos
                  </>
                )}
              </button>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow p-12 text-center">
              <Shield size={48} className="mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Selecciona un usuario
              </h3>
              <p className="text-gray-500">
                Elige un usuario de la lista para configurar sus permisos de acceso a módulos
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Nota informativa */}
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
        <div className="flex gap-3">
          <AlertCircle size={20} className="text-emerald-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-emerald-800">
            <p className="font-medium mb-1">Información importante:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Los administradores tienen acceso a todos los módulos por defecto</li>
              <li>Los permisos solo controlan la visibilidad de los módulos en el menú</li>
              <li>Los cambios se aplican inmediatamente al iniciar sesión</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
